import React, { useState, useEffect, useRef, memo } from 'react';
import { Play, RotateCcw, MessageSquare, Send, User, Bot } from 'lucide-react';
import { useVariables } from '../contexts/VariableContext';
import { API_BASE_URL } from '../services/api';
import { validateFlow, flowExecutionLimiter } from '../utils/security';
import { renderMarkdown } from '../utils/markdown';
import { NodeValidatorFactory } from '../utils/nodeValidators';
import FlowValidator from '../utils/flowValidator';
import { buildValidationError } from '../utils/errorMessages';

const PreviewPlayer = memo(({ flow, onClose, onNodeHighlight }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentNodeId, setCurrentNodeId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [waitingForInput, setWaitingForInput] = useState(false);
  const [availableButtons, setAvailableButtons] = useState([]);
  const visitedNodesRef = useRef(new Set());
  const currentNodeIdRef = useRef(null);
  const messagesEndRef = useRef(null);
  const executionTimeoutRef = useRef(null);
  const stepCountRef = useRef(0);

  const { replaceVariables, setVariable, clearVariables } = useVariables();

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const addMessage = (content, type = 'bot', nodeId = null) => {
    // No HTML-escaping: this renders as a React text child (JSX auto-escapes
    // for the DOM), never via dangerouslySetInnerHTML. Escaping here only
    // produced corrupted visitor-facing text (e.g. "Let's" -> "Let&#x27;s")
    // — same bug class already fixed in security.js/nodeProcessors.js.
    const displayContent = typeof content === 'string' ? content : String(content);
    const message = { id: Date.now() + Math.random(), content: displayContent, type, timestamp: new Date(), nodeId };
    setMessages(prev => [...prev, message]);
    return message;
  };

  const findStartNode = () => {
    if (!flow || !Array.isArray(flow.nodes) || flow.nodes.length === 0) return null;
    const trigger = flow.nodes.find((n) => n.type === 'trigger');
    if (trigger) return trigger;
    const noIncoming = flow.nodes.find((node) => !flow.edges?.some((edge) => edge.target === node.id));
    if (noIncoming) return noIncoming;
    return flow.nodes[0] || null;
  };

  const resetVisited = () => {
    visitedNodesRef.current = new Set();
  };

  const getAllOutgoing = (nodeId) => flow.edges.filter((e) => e.source === nodeId);

  const getNextNodes = (nodeId, outputHandle = null) => {
    const outgoing = getAllOutgoing(nodeId);
    if (outgoing.length === 0) return [];
    if (!outputHandle) {
      return outgoing.map((e) => flow.nodes.find((n) => n.id === e.target)).filter(Boolean);
    }
    const exact = outgoing.filter((e) => e.sourceHandle === outputHandle);
    if (exact.length > 0) {
      return exact.map((e) => flow.nodes.find((n) => n.id === e.target)).filter(Boolean);
    }
    const fallback = outgoing.filter((e) => !e.sourceHandle || e.sourceHandle === 'success');
    return fallback.map((e) => flow.nodes.find((n) => n.id === e.target)).filter(Boolean);
  };

  const executeNode = async (node) => {
    if (!node) return null;

    const validation = NodeValidatorFactory.validateNode(node);
    if (!validation.valid) {
      addMessage(`Node validation failed: ${validation.errors.join(', ')}`, 'bot', node.id);
      return 'error';
    }

    setCurrentNodeId(node.id);
    currentNodeIdRef.current = node.id;
    onNodeHighlight?.(node.id);
    visitedNodesRef.current.add(node.id);

    await new Promise(resolve => setTimeout(resolve, 300));

    try {
      const base = (API_BASE_URL || '/api').replace(/\/$/, '');

      switch (node.type) {
        case 'message': {
          const messageText = replaceVariables(node.data.config?.message || 'Hello!', node.id);
          addMessage(messageText, 'bot', node.id);
          return 'success';
        }

        case 'button': {
          const buttons = node.data.config?.buttons || [];
          if (buttons.length > 0) {
            setAvailableButtons(buttons.map((btn, index) => ({ ...btn, id: index, nodeId: node.id })));
            setWaitingForInput(true);
            return 'waiting';
          }
          return 'success';
        }

        case 'prompt': {
          const promptText = replaceVariables(node.data.config?.prompt || 'Please enter your response:', node.id);
          addMessage(promptText, 'bot', node.id);
          setWaitingForInput(true);
          return 'waiting';
        }

        case 'setVariable': {
          const varName = node.data.config?.variableName;
          let varValue = node.data.config?.value || '';
          const scope = node.data.config?.scope || 'local';

          if (varValue.includes('{{') && varValue.includes('}}')) {
            const actualValue = replaceVariables(varValue, node.id);
            if (actualValue === varValue && actualValue.includes('{{')) {
              const selectedButtonValue = replaceVariables('{{selectedButton}}', node.id);
              varValue = selectedButtonValue !== '{{selectedButton}}' ? selectedButtonValue : varValue;
            } else {
              varValue = actualValue;
            }
          } else {
            varValue = replaceVariables(varValue, node.id);
          }

          if (varName) {
            setVariable(varName, varValue, scope, node.id);
            addMessage(`Set ${scope} variable: ${varName} = ${varValue}`, 'system', node.id);
          }
          return 'success';
        }

        case 'condition': {
          const condition = node.data.config?.condition || 'true';
          const result = replaceVariables(condition, node.id);
          return result === 'true' || result === true ? 'true' : 'false';
        }

        case 'switch': {
          const switchVar = node.data.config?.variable;
          const switchValue = replaceVariables(`{{${switchVar}}}`, node.id);
          const cases = node.data.config?.cases || [];
          const matchingCase = cases.findIndex(c => c.value === switchValue);
          return matchingCase >= 0 ? `case-${matchingCase}` : 'default';
        }

        case 'end': {
          const endMessage = replaceVariables(node.data.config?.message || 'Conversation ended.', node.id);
          addMessage(endMessage, 'bot', node.id);
          setIsPlaying(false);
          return 'end';
        }

        case 'trigger': {
          addMessage('Flow started', 'system', node.id);
          return 'success';
        }

        case 'llm': {
          const llmPrompt = replaceVariables(node.data.config?.prompt || 'Hello, how can I help you?', node.id);
          const systemPrompt = replaceVariables(node.data.config?.systemPrompt || 'You are a helpful assistant.', node.id);
          addMessage(`Thinking…`, 'system', node.id);
          try {
            const response = await fetch(`${base}/ai/chat`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ message: llmPrompt, systemPrompt })
            });
            if (response.ok) {
              const data = await response.json();
              const llmResponse = data.reply || 'No response from AI model.';
              addMessage(llmResponse, 'bot', node.id);
              setVariable(node.data.config?.outputVariable || 'llm_response', llmResponse, 'local', node.id);
              return 'success';
            } else {
              throw new Error(`AI request failed (${response.status})`);
            }
          } catch (err) {
            addMessage(`LLM Error: ${err.message}`, 'bot', node.id);
            return 'error';
          }
        }

        case 'apiCall': {
          const apiUrl = replaceVariables(node.data.config?.url || '', node.id);
          const apiMethod = node.data.config?.method || 'GET';

          // SSRF guard: block private IPs and non-HTTP protocols
          if (apiUrl) {
            try {
              const parsed = new URL(apiUrl);
              const blockedHosts = ['localhost', '127.0.0.1', '0.0.0.0', '::1'];
              const hostname = parsed.hostname.toLowerCase();
              if (
                blockedHosts.includes(hostname) ||
                hostname.startsWith('192.168.') ||
                hostname.startsWith('10.') ||
                /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname)
              ) {
                addMessage(`API Call blocked: private/local IP not allowed (${apiUrl})`, 'system', node.id);
                return 'error';
              }
              if (!['http:', 'https:'].includes(parsed.protocol)) {
                addMessage(`API Call blocked: unsupported protocol (${parsed.protocol})`, 'system', node.id);
                return 'error';
              }
            } catch {
              addMessage(`API Call blocked: invalid URL (${apiUrl})`, 'system', node.id);
              return 'error';
            }
          }

          addMessage(`API Call: ${apiMethod} ${apiUrl}`, 'system', node.id);
          try {
            const fetchOptions = { method: apiMethod, headers: node.data.config?.headers || {} };
            if (node.data.config?.body && apiMethod !== 'GET') {
              fetchOptions.body = replaceVariables(node.data.config.body, node.id);
              fetchOptions.headers['Content-Type'] = fetchOptions.headers['Content-Type'] || 'application/json';
            }
            const apiRes = await fetch(apiUrl, fetchOptions);
            const responseText = await apiRes.text();
            setVariable('api_response', responseText, 'local', node.id);
            addMessage(`API Response (${apiRes.status}): ${responseText.slice(0, 300)}`, 'system', node.id);
            return apiRes.ok ? 'success' : 'error';
          } catch (err) {
            addMessage(`API Call Error: ${err.message}`, 'bot', node.id);
            return 'error';
          }
        }

        case 'function': {
          const funcName = node.data.config?.functionName || 'identity';
          const inputVar = node.data.config?.inputVariable || '';
          const outputVar = node.data.config?.outputVariable || 'result';
          const inputValue = replaceVariables(`{{${inputVar}}}`, node.id);
          addMessage(`Function: ${funcName}(${inputValue})`, 'system', node.id);

          let funcResult = inputValue;
          switch (funcName) {
            case 'upper': funcResult = String(inputValue).toUpperCase(); break;
            case 'lower': funcResult = String(inputValue).toLowerCase(); break;
            case 'length': funcResult = String(inputValue).length; break;
            case 'trim': funcResult = String(inputValue).trim(); break;
            case 'reverse': funcResult = String(inputValue).split('').reverse().join(''); break;
            case 'sum': {
              const nums = String(inputValue).split(',').map(n => parseFloat(n.trim())).filter(n => !isNaN(n));
              funcResult = nums.reduce((a, b) => a + b, 0);
              break;
            }
            default: funcResult = `${funcName}(${inputValue})`;
          }

          setVariable(outputVar, funcResult, 'local', node.id);
          addMessage(`Result: ${funcResult}`, 'system', node.id);
          return 'success';
        }

        case 'dataInput': {
          const dataSource = node.data.config?.source || 'manual';
          const data = node.data.config?.data || '';
          addMessage(`Data Input (${dataSource}): ${data}`, 'system', node.id);
          setVariable('input_data', data, 'local', node.id);
          return 'success';
        }

        case 'dataOutput': {
          const outputFormat = node.data.config?.format || 'json';
          const outputData = replaceVariables(node.data.config?.data || '{{result}}', node.id);
          addMessage(`Data Output (${outputFormat}): ${outputData}`, 'system', node.id);
          return 'success';
        }

        case 'intent': {
          const intentInput = replaceVariables(`{{${node.data.config?.inputVariable || 'userMessage'}}}`, node.id);
          const intents = node.data.config?.intents || [];
          let detectedIntent = 'unknown';
          for (const intent of intents) {
            if (intent.keywords?.some(kw => intentInput.toLowerCase().includes(kw.toLowerCase()))) {
              detectedIntent = intent.name;
              break;
            }
          }
          addMessage(`Intent detected: ${detectedIntent}`, 'system', node.id);
          setVariable('detected_intent', detectedIntent, 'local', node.id);
          return detectedIntent;
        }

        case 'textAnalysis': {
          const analysisType = node.data.config?.analysisType || 'sentiment';
          const textInput = replaceVariables(`{{${node.data.config?.inputVariable || 'text'}}}`, node.id);
          addMessage(`Text Analysis (${analysisType}): ${textInput}`, 'system', node.id);
          try {
            const response = await fetch(`${base}/ai/chat`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                message: `Perform ${analysisType} analysis on: "${textInput}". Respond with a JSON object only.`,
                systemPrompt: 'You are a text analysis AI. Respond only with a valid JSON object containing the analysis results.'
              })
            });
            if (response.ok) {
              const data = await response.json();
              const analysisResult = data.reply || '{"result":"unknown"}';
              setVariable('analysis_result', analysisResult, 'local', node.id);
              addMessage(`Analysis: ${analysisResult}`, 'bot', node.id);
              return 'success';
            } else {
              throw new Error(`Analysis failed (${response.status})`);
            }
          } catch (err) {
            addMessage(`Text Analysis Error: ${err.message}`, 'bot', node.id);
            return 'error';
          }
        }

        case 'integration': {
          const integrationType = node.data.config?.integrationType || 'webhook';
          const integrationUrl = node.data.config?.url || '';
          const integrationMessage = replaceVariables(node.data.config?.message || '', node.id);
          addMessage(`Integration (${integrationType}): ${integrationUrl}`, 'system', node.id);
          if (integrationMessage) addMessage(`Message: ${integrationMessage}`, 'system', node.id);
          return 'success';
        }

        case 'loop': {
          const loopType = node.data.config?.loopType || 'forEach';
          const arrayVar = node.data.config?.arrayVariable || 'items';
          const itemVar = node.data.config?.itemVariable || 'item';
          const arrayValue = replaceVariables(`{{${arrayVar}}}`, node.id);
          let arrayData = [];
          try {
            if (arrayValue.startsWith('[') || arrayValue.startsWith('{')) {
              arrayData = JSON.parse(arrayValue);
            } else {
              arrayData = arrayValue.includes(',') ? arrayValue.split(',').map(s => s.trim()) : [arrayValue];
            }
          } catch {
            arrayData = [arrayValue];
          }
          if (!Array.isArray(arrayData)) arrayData = [arrayData];

          if (arrayData.length > 0) {
            setVariable(itemVar, arrayData[0], 'local', node.id);
            addMessage(`Loop (${loopType}): ${arrayData.length} items, processing first: ${arrayData[0]}`, 'system', node.id);
          } else {
            addMessage('Loop: no items to process', 'system', node.id);
          }
          return 'success';
        }

        case 'code': {
          addMessage('Code execution is disabled for security. Use a function node instead.', 'system', node.id);
          return 'error';
        }

        case 'dataTransform': {
          const transformExpr = node.data.config?.transform || '';
          const transformOutputVar = node.data.config?.outputVariable || 'transformed';
          const transformResult = replaceVariables(transformExpr, node.id);
          setVariable(transformOutputVar, transformResult, 'local', node.id);
          addMessage(`Transform: ${transformExpr} → ${transformResult}`, 'system', node.id);
          return 'success';
        }

        case 'imageGen': {
          const imagePrompt = replaceVariables(node.data.config?.prompt || 'Generate an image', node.id);
          addMessage(`Image Generation: "${imagePrompt}" — Image generation requires an external API key and is not available in preview mode.`, 'system', node.id);
          return 'error';
        }

        default: {
          addMessage(`Executed ${node.type} node`, 'system', node.id);
          return 'success';
        }
      }
    } catch (err) {
      addMessage(`Node execution error: ${err.message}`, 'bot', node.id);
      return 'error';
    }
  };

  const continueExecution = async (outputHandle = 'success') => {
    const sourceId = currentNodeIdRef.current || currentNodeId;
    if (!sourceId) return;

    stepCountRef.current++;
    if (stepCountRef.current > 1000) {
      addMessage('Flow exceeded 1000 steps — possible infinite loop. Stopping.', 'bot');
      setIsPlaying(false);
      if (executionTimeoutRef.current) clearTimeout(executionTimeoutRef.current);
      return;
    }

    const nextNodes = getNextNodes(sourceId, outputHandle);
    if (nextNodes.length > 0) {
      const nextNode = nextNodes[0];
      const result = await executeNode(nextNode);
      currentNodeIdRef.current = nextNode.id;

      if (result === 'waiting' || result === 'end') return;
      await continueExecution(result);
    } else {
      setIsPlaying(false);
      addMessage('Flow completed.', 'bot');
      if (executionTimeoutRef.current) clearTimeout(executionTimeoutRef.current);
    }
  };

  const startPreview = async () => {
    try {
      const clientId = 'preview-' + (window.location.hostname || 'localhost');
      if (!flowExecutionLimiter.isAllowed(clientId)) {
        addMessage('Rate limit exceeded. Please wait before starting another preview.', 'bot');
        return;
      }

      const basicValidation = validateFlow(flow);
      if (!basicValidation.valid) {
        const errorInfo = buildValidationError(basicValidation.errors, basicValidation.warnings);
        addMessage(errorInfo.message, 'bot');
        errorInfo.actions?.forEach(action => addMessage(`  • ${action}`, 'bot'));
        return;
      }

      const advancedValidator = new FlowValidator();
      const advancedValidation = advancedValidator.validate(flow);
      if (!advancedValidation.valid) {
        const errorInfo = buildValidationError(advancedValidation.errors, advancedValidation.warnings);
        addMessage(errorInfo.message, 'bot');
        advancedValidation.errors?.forEach(e => addMessage(`  • ${e}`, 'bot'));
        return;
      }
      if (advancedValidation.warnings.length > 0) {
        addMessage(`Note: ${advancedValidation.warnings.join('; ')}`, 'bot');
      }

      setIsPlaying(true);
      setMessages([]);
      setCurrentNodeId(null);
      setWaitingForInput(false);
      setAvailableButtons([]);
      clearVariables('local');
      resetVisited();
      stepCountRef.current = 0;

      executionTimeoutRef.current = setTimeout(() => {
        addMessage('Flow execution timed out after 5 minutes.', 'bot');
        setIsPlaying(false);
      }, 5 * 60 * 1000);

      if (!flow || !Array.isArray(flow.nodes)) {
        addMessage('Invalid flow: no nodes found', 'system');
        setIsPlaying(false);
        clearTimeout(executionTimeoutRef.current);
        return;
      }

      const startNode = findStartNode();
      if (startNode) {
        const result = await executeNode(startNode);
        if (result !== 'waiting' && result !== 'end') {
          await continueExecution(result);
        }
      } else {
        addMessage('No start node found in flow. Add a Trigger node to begin.', 'bot');
        setIsPlaying(false);
      }
    } catch (err) {
      addMessage(`Preview error: ${err?.message || err}`, 'bot');
      setIsPlaying(false);
    }
  };

  const handleUserInput = async () => {
    if (!userInput.trim() && availableButtons.length === 0) return;

    addMessage(userInput || 'Button clicked', 'user');

    if (currentNodeId) {
      const currentNode = flow.nodes.find(n => n.id === currentNodeId);
      if (currentNode?.type === 'prompt' && currentNode.data.config?.variable) {
        setVariable(currentNode.data.config.variable, userInput, 'local', currentNodeId);
      }
    }

    setUserInput('');
    setWaitingForInput(false);
    setAvailableButtons([]);
    await continueExecution('success');
  };

  const handleButtonClick = async (button) => {
    addMessage(button.text, 'user');
    setVariable('selectedButton', button.text, 'global', button.nodeId);

    if (button.value && button.value !== button.text) {
      setVariable(button.value.toLowerCase(), button.text, 'global', button.nodeId);
    }

    // Auto-detect common variable patterns from button text
    const buttonText = button.text.toLowerCase();
    if (/excited|happy|sad|angry|calm|energetic|high|low|medium/.test(buttonText)) {
      setVariable('emotion', button.text, 'global', button.nodeId);
    }
    if (/rock|pop|jazz|classical|hip hop|country|electronic|blues|folk/.test(buttonText)) {
      setVariable('genre', button.text, 'global', button.nodeId);
    }
    if (/first person|third person|narrator|omniscient/.test(buttonText)) {
      setVariable('perspective', button.text, 'global', button.nodeId);
    }
    if (/formal|casual|professional|friendly|serious/.test(buttonText)) {
      setVariable('style', button.text, 'global', button.nodeId);
    }

    setAvailableButtons([]);
    setWaitingForInput(false);
    await continueExecution(`button-${button.id}`);
  };

  const resetPreview = () => {
    setIsPlaying(false);
    setMessages([]);
    setCurrentNodeId(null);
    setWaitingForInput(false);
    setAvailableButtons([]);
    setUserInput('');
    clearVariables('local');
    onNodeHighlight?.(null);
    stepCountRef.current = 0;
    resetVisited();
    if (executionTimeoutRef.current) {
      clearTimeout(executionTimeoutRef.current);
      executionTimeoutRef.current = null;
    }
  };

  const visibleMessages = messages.filter(m => m.type !== 'system');

  return (
    <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden" style={{ height: '85vh' }}>
      {/* Chat Header */}
      <div className="flex items-center justify-between px-5 py-4 bg-blue-600 text-white">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
            <Bot size={18} className="text-white" />
          </div>
          <div>
            <div className="font-semibold text-sm">AI Assistant</div>
            <div className={`text-xs flex items-center gap-1 ${isPlaying ? 'text-green-200' : 'text-blue-200'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${isPlaying ? 'bg-green-300 animate-pulse' : 'bg-blue-300'}`} />
              {isPlaying ? 'Active' : 'Ready'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isPlaying ? (
            <button
              onClick={startPreview}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-medium transition-colors"
            >
              <Play size={13} /> Start
            </button>
          ) : (
            <button
              onClick={resetPreview}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-medium transition-colors"
            >
              <RotateCcw size={13} /> Restart
            </button>
          )}
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors ml-1">
            <span className="text-xl leading-none">×</span>
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
        {visibleMessages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center text-gray-400 gap-3">
            <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center">
              <MessageSquare size={24} className="text-blue-400" />
            </div>
            <div>
              <div className="font-medium text-gray-500">Ready to preview</div>
              <div className="text-sm">Press Start to run your flow</div>
            </div>
          </div>
        )}

        {visibleMessages.map((message) => (
          <div key={message.id} className={`flex gap-2 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
            {message.type === 'bot' && (
              <div className="w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <Bot size={13} className="text-white" />
              </div>
            )}
            <div className={`max-w-[78%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
              message.type === 'user'
                ? 'bg-blue-600 text-white rounded-br-sm'
                : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-sm'
            }`}>
              {renderMarkdown(message.content)}
            </div>
            {message.type === 'user' && (
              <div className="w-7 h-7 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <User size={13} className="text-gray-600" />
              </div>
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="p-4 border-t border-gray-100 bg-white">
        {waitingForInput && availableButtons.length > 0 ? (
          <div className="space-y-2">
            {availableButtons.map((button) => (
              <button
                key={button.id}
                onClick={() => handleButtonClick(button)}
                className="w-full py-2.5 px-4 text-sm text-left bg-blue-50 hover:bg-blue-100 text-blue-700 font-medium rounded-xl transition-colors border border-blue-100"
              >
                {button.text}
              </button>
            ))}
          </div>
        ) : (
          <div className={`flex gap-2 ${!waitingForInput ? 'opacity-50 pointer-events-none' : ''}`}>
            <input
              type="text"
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleUserInput()}
              placeholder={waitingForInput ? 'Type your message…' : 'Waiting for flow…'}
              className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              disabled={!waitingForInput}
            />
            <button
              onClick={handleUserInput}
              disabled={!waitingForInput}
              className="w-10 h-10 bg-blue-600 text-white rounded-xl flex items-center justify-center hover:bg-blue-700 transition-colors flex-shrink-0"
            >
              <Send size={15} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

PreviewPlayer.displayName = 'PreviewPlayer';

export default PreviewPlayer;
